/**
 * Backup Settings Component
 * Manual backup/restore + shadow-backup recovery
 *
 * @version 2.0.0
 */

import React, { useState, useEffect, useRef } from 'react';
import autoBackupManager from '../utils/autoBackupManager';
import persistentStorage from '../utils/persistentStorage';
import dataManager from '../utils/DataManager';
import shadowBackup from '../utils/shadowBackup';
import CloudSaveModal from './CloudSaveModal';
import authManager from '../utils/authManager';
import * as googleDrive from '../utils/googleDrive';

const BTN = {
  green:  { background: 'linear-gradient(135deg,#C4714A 0%,#D4895F 100%)', shadow: 'rgba(196,113,74,.3)' },
  blue:   { background: 'linear-gradient(135deg,#2D5A6E 0%,#5B8FA8 100%)', shadow: 'rgba(45,90,110,.3)' },
  purple: { background: 'linear-gradient(135deg,#2D5A6E 0%,#5B8FA8 100%)', shadow: 'rgba(45,90,110,.3)' },
  amber:  { background: 'linear-gradient(135deg,#C4714A 0%,#E08A52 100%)', shadow: 'rgba(196,113,74,.3)' },
  red:    { background: '#e74c3c', shadow: 'rgba(231,76,60,.3)' },
};

const ActionButton = ({ color = 'green', onClick, disabled, children, style = {} }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    style={{
      background: BTN[color].background,
      color: 'white',
      border: 'none',
      padding: '1rem 2rem',
      borderRadius: '12px',
      fontSize: '1rem',
      fontWeight: '600',
      cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.6 : 1,
      boxShadow: `0 4px 12px ${BTN[color].shadow}`,
      transition: 'all .2s',
      width: '100%',
      ...style,
    }}
  >
    {children}
  </button>
);

const BackupSettings = () => {
  const [backupStatus, setBackupStatus]   = useState(null);
  const [storageInfo, setStorageInfo]     = useState(null);
  const [tripCount, setTripCount]         = useState(0);
  const [shadowInfo, setShadowInfo]       = useState(null);

  const [loading, setLoading]             = useState(false);
  const [restoring, setRestoring]         = useState(false);
  const [preview, setPreview]             = useState(null);
  const [previewError, setPreviewError]   = useState(null);
  const [showCloudModal, setShowCloudModal] = useState(false);
  const [driveConnected, setDriveConnected] = useState(googleDrive.isConnected());
  const [driveBackups, setDriveBackups]   = useState(null);
  const [driveBusy, setDriveBusy]         = useState(false);
  const [advancedOpen, setAdvancedOpen]   = useState(false);

  const fileInputRef = useRef(null);

  // Drive connection status (authoritative, against the backend).
  useEffect(() => {
    if (authManager.isLoggedIn()) googleDrive.checkConnected().then(setDriveConnected).catch(() => {});
  }, []);

  // List the Drive backups whenever connected.
  useEffect(() => {
    let cancelled = false;
    if (!driveConnected) { setDriveBackups(null); return; }
    setDriveBusy(true);
    googleDrive.listBackups()
      .then(list => { if (!cancelled) setDriveBackups(list); })
      .catch(() => { if (!cancelled) setDriveBackups([]); })
      .finally(() => { if (!cancelled) setDriveBusy(false); });
    return () => { cancelled = true; };
  }, [driveConnected]);

  const connectDrive = async () => {
    if (!authManager.isLoggedIn()) { alert('Inicia sesión para conectar Google Drive.'); return; }
    try { await googleDrive.connect(); } // full-page redirect to Google
    catch (e) { alert(`No se pudo iniciar la conexión con Drive: ${e.message || e}`); }
  };

  const disconnectDrive = async () => {
    if (!confirm('¿Desconectar Google Drive? Tus backups ya guardados no se borran.')) return;
    await googleDrive.disconnect();
    setDriveConnected(false);
  };

  const restoreFromDrive = async (file) => {
    if (!confirm(`¿Restaurar "${file.name}"?\n\nSe fusionará con tus viajes actuales.`)) return;
    setDriveBusy(true);
    try {
      const text = await googleDrive.downloadBackupContent(file.id);
      const f = new File([text], file.name, { type: 'application/json' });
      const result = await autoBackupManager.restoreFromBackup(f);
      alert(`✅ Restaurado: ${result.tripsRestored} viaje(s).`);
      loadSettings();
    } catch (e) {
      alert(`❌ Error al restaurar: ${e.message || e}`);
    } finally {
      setDriveBusy(false);
    }
  };

  useEffect(() => { loadSettings(); }, []);

  const loadSettings = async () => {
    try {
      setBackupStatus(autoBackupManager.getBackupStatus());
      setStorageInfo(await persistentStorage.getStorageInfo());
      const trips = await dataManager.getAllTrips();
      setTripCount(trips.length);
      setShadowInfo(shadowBackup.getInfo());
    } catch (e) {
      console.error('Error loading backup settings:', e);
    }
  };

  // ── Manual backup ─────────────────────────────────────────────────────────
  const handleManualBackup = async () => {
    setLoading(true);
    try {
      const backup   = await autoBackupManager.createBackup();
      const filename = await autoBackupManager.downloadBackup(backup);
      alert(
        `✅ Backup descargado exitosamente\n\n` +
        `Archivo: ${filename}\n` +
        `Viajes: ${backup.stats.totalTrips}\n` +
        `Fotos: ${backup.stats.totalPhotos}\n` +
        `Notas: ${backup.stats.totalNotes}\n\n` +
        `Guárdalo en un lugar seguro.`
      );
      loadSettings();
    } catch (e) {
      alert(`❌ Error al crear backup: ${e.message}`);
    } finally {
      setLoading(false);
    }
  };

  // ── Phase 1: analyse file (no data changes) ──────────────────────────────
  const handleFileSelected = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    setPreviewError(null);
    setPreview(null);
    setRestoring(true);
    try {
      const info = await autoBackupManager.analyzeBackupFile(file);
      setPreview({ ...info, file });
    } catch (e) {
      setPreviewError(e.message);
    } finally {
      setRestoring(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // ── Phase 2: confirmed → execute restore ─────────────────────────────────
  const handleConfirmRestore = async () => {
    if (!preview?.file) return;
    setRestoring(true);
    try {
      const result = await autoBackupManager.restoreFromBackup(preview.file);
      let msg = `✅ Backup restaurado\n\nViajes restaurados: ${result.tripsRestored}`;
      if (result.tripsFailed > 0) {
        msg += `\nViajes con errores: ${result.tripsFailed}`;
        result.errors.forEach(e => { msg += `\n - ${e.destination}: ${e.error}`; });
      }
      if (result.backupDate) {
        try { msg += `\n\nFecha del backup: ${new Date(result.backupDate).toLocaleString('es-ES')}`; } catch (_) {}
      }
      alert(msg);
      window.location.reload();
    } catch (e) {
      alert(`❌ Error al restaurar: ${e.message}`);
    } finally {
      setRestoring(false);
      setPreview(null);
    }
  };

  const handleCancelPreview = () => {
    setPreview(null);
    setPreviewError(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // ── Shadow backup recovery ────────────────────────────────────────────────
  const handleShadowRestore = async () => {
    const info = shadowBackup.getInfo();
    const confirmed = confirm(
      `⚠️ Recuperar desde memoria del navegador\n\n` +
      `Guardado: ${info?.savedAt?.toLocaleString('es-ES') || 'desconocido'}\n` +
      `Viajes: ${info?.tripCount || '?'}\n\n` +
      `Nota: Las fotos NO están incluidas.\n\n¿Continuar?`
    );
    if (!confirmed) return;
    setRestoring(true);
    try {
      const result = await autoBackupManager.restoreFromShadow();
      alert(
        `✅ Recuperación completada\n\n` +
        `Viajes recuperados: ${result.tripsRestored}\n` +
        `⚠️ Las fotos no se incluyen en la recuperación desde memoria.\n\n` +
        `Descarga un backup completo ahora para protegerlas.`
      );
      window.location.reload();
    } catch (e) {
      alert(`❌ Error: ${e.message}`);
    } finally {
      setRestoring(false);
    }
  };

  const handleRequestPersistence = async () => {
    setLoading(true);
    try {
      const result = await persistentStorage.requestPersistence();
      alert(result.granted
        ? `✅ ${result.message}\n\nTus datos están ahora protegidos.`
        : `⚠️ ${result.message}\n\nHaz backups regulares.`
      );
      loadSettings();
    } catch (e) {
      alert(`❌ Error: ${e.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleBackups = () => {
    if (backupStatus?.disabled) {
      autoBackupManager.enableBackups();
      alert('✅ Recordatorios de backup activados');
    } else {
      if (confirm('¿Desactivar recordatorios de backup?\n\nPodrás seguir haciendo backups manualmente.')) {
        autoBackupManager.disableBackups();
      }
    }
    loadSettings();
  };

  // ── Layout helpers ────────────────────────────────────────────────────────
  const Card = ({ children, bg = '#f5f1eb', border = null }) => (
    <div style={{ background: bg, padding: '1.5rem', borderRadius: '12px', marginBottom: '1.5rem', borderLeft: border ? `4px solid ${border}` : undefined }}>
      {children}
    </div>
  );

  const STitle = ({ text }) => (
    <h3 style={{ color: '#8B7355', marginBottom: '1rem', fontSize: '1.1rem' }}>{text}</h3>
  );

  return (
    <div style={{ background: 'white', borderRadius: '20px', padding: '2rem', boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}>

      <h2 style={{ color: '#8B7355', marginBottom: '1.5rem', fontSize: '1.8rem', fontWeight: '900' }}>
        Gestión de Backups
      </h2>

      {/* Backup status */}
      <Card>
        <STitle text="Estado del Backup" />
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', fontSize: '0.95rem' }}>
          <div>
            <strong>Último backup:</strong>
            <div style={{ color: '#666', marginTop: '0.25rem' }}>{backupStatus?.lastBackupDate || 'Nunca'}</div>
          </div>
          <div>
            <strong>Días desde backup:</strong>
            <div style={{ color: backupStatus?.daysSinceLastBackup > 14 ? '#e74c3c' : '#666', marginTop: '0.25rem' }}>
              {backupStatus?.daysSinceLastBackup === 999 ? 'N/A' : `${backupStatus?.daysSinceLastBackup} días`}
            </div>
          </div>
          <div>
            <strong>Total de viajes:</strong>
            <div style={{ color: '#666', marginTop: '0.25rem' }}>{tripCount}</div>
          </div>
          <div>
            <strong>Recordatorios:</strong>
            <div style={{ color: '#666', marginTop: '0.25rem' }}>
              {backupStatus?.disabled ? '🔴 Desactivados' : '🟢 Activados'}
            </div>
          </div>
        </div>
      </Card>

      {/* Manual backup + cloud share */}
      <div style={{ marginBottom: '1.5rem' }}>
        <STitle text="Guardar Backup" />
        <ActionButton color="green" onClick={handleManualBackup} disabled={loading} style={{ marginBottom: '0.75rem' }}>
          {loading ? '⏳ Creando backup...' : '📥 Descargar Backup'}
        </ActionButton>
        <ActionButton color="blue" onClick={() => setShowCloudModal(true)} disabled={loading}>
          ☁️ Guardar en tu Nube
        </ActionButton>
        <p style={{ color: '#666', fontSize: '0.85rem', marginTop: '0.75rem', lineHeight: '1.5' }}>
          <strong>Descargar</strong> guarda el archivo en tu dispositivo.{' '}
          <strong>Guardar en tu Nube</strong> abre Google Drive, iCloud, Dropbox u otras apps —{' '}
          tus datos nunca pasan por nuestros servidores.
        </p>
      </div>

      {showCloudModal && <CloudSaveModal onClose={() => { setShowCloudModal(false); loadSettings(); }} />}

      {/* Google Drive — automatic backups to the user's own Drive */}
      <Card border={driveConnected ? '#4a7c59' : '#5B8FA8'}>
        <STitle text="Google Drive" />
        {driveConnected ? (
          <>
            <p style={{ color: '#4a7c59', fontWeight: 600, margin: '0 0 0.75rem' }}>
              ✓ Conectado — al guardar el viaje, la copia sube sola a la carpeta «{`Wayfare Backups`}» de tu Drive.
            </p>

            {/* Copies in Drive — list + restore */}
            <div style={{ margin: '0 0 0.75rem' }}>
              <div style={{ fontSize: '0.85rem', color: '#8B7355', fontWeight: 600, marginBottom: '0.4rem' }}>
                Copias en Drive {driveBusy ? '…' : ''}
              </div>
              {driveBackups && driveBackups.length === 0 && !driveBusy && (
                <p style={{ color: '#999', fontSize: '0.85rem', margin: 0 }}>Aún no hay copias. Pulsa «Salvar viaje».</p>
              )}
              {driveBackups && driveBackups.map(f => (
                <div key={f.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem', padding: '0.5rem 0', borderBottom: '1px solid #efe9df' }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: '0.85rem', color: '#5C4A32', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</div>
                    <div style={{ fontSize: '0.72rem', color: '#aaa' }}>
                      {(() => { try { return new Date(f.modifiedTime).toLocaleString('es-ES'); } catch { return ''; } })()}
                    </div>
                  </div>
                  <button
                    onClick={() => restoreFromDrive(f)}
                    disabled={driveBusy}
                    style={{ flexShrink: 0, background: '#2D5A6E', color: '#fff', border: 'none', borderRadius: '8px', padding: '0.4rem 0.8rem', fontSize: '0.8rem', fontWeight: 600, cursor: driveBusy ? 'not-allowed' : 'pointer' }}
                  >
                    Restaurar
                  </button>
                </div>
              ))}
            </div>

            <ActionButton color="red" onClick={disconnectDrive}>Desconectar Drive</ActionButton>
          </>
        ) : (
          <>
            <p style={{ color: '#666', fontSize: '0.9rem', margin: '0 0 0.75rem', lineHeight: 1.5 }}>
              Conecta tu Google Drive para que los backups se guarden automáticamente en la
              carpeta «Wayfare Backups» de tu Drive. Tus datos van directos a tu Drive — nunca
              pasan por nuestros servidores.{' '}
              <a href="./privacy.html" target="_blank" rel="noopener noreferrer" style={{ color: '#2D5A6E' }}>
                Política de privacidad
              </a>.
            </p>
            <ActionButton color="blue" onClick={connectDrive}>☁️ Conectar Google Drive</ActionButton>
          </>
        )}
      </Card>

      {/* Advanced — technical backup tools (collapsed by default) */}
      <button
        onClick={() => setAdvancedOpen(o => !o)}
        style={{ width: '100%', background: 'none', border: '1px solid #E8DDD0', borderRadius: '12px', padding: '0.85rem', color: '#8B7355', fontSize: '0.95rem', fontWeight: 600, cursor: 'pointer', marginBottom: '1.5rem' }}
      >
        {advancedOpen ? '▴ Ocultar opciones avanzadas' : '▾ Opciones avanzadas'}
      </button>

      {advancedOpen && (<>
      {/* Restore from file */}
      <div style={{ marginBottom: '1.5rem' }}>
        <STitle text="Restaurar desde Archivo" />

        {!preview && (
          <>
            <label
              htmlFor="restore-file"
              style={{
                display: 'block', background: BTN.blue.background, color: 'white',
                padding: '1rem 2rem', borderRadius: '12px', fontSize: '1rem', fontWeight: '600',
                cursor: restoring ? 'not-allowed' : 'pointer', textAlign: 'center',
                boxShadow: `0 4px 12px ${BTN.blue.shadow}`, opacity: restoring ? 0.6 : 1,
              }}
            >
              {restoring ? '⏳ Analizando archivo...' : '📂 Seleccionar Archivo de Backup'}
            </label>
            <input id="restore-file" ref={fileInputRef} type="file" accept=".json"
              onChange={handleFileSelected} disabled={restoring} style={{ display: 'none' }} />
          </>
        )}

        {previewError && (
          <div style={{ background: '#fdecea', border: '2px solid #e74c3c', borderRadius: '12px', padding: '1.25rem', marginTop: '1rem' }}>
            <strong style={{ color: '#c0392b' }}>❌ No se pudo leer el archivo</strong>
            <pre style={{ color: '#555', fontSize: '0.85rem', marginTop: '0.5rem', whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}>
              {previewError}
            </pre>
            <button onClick={handleCancelPreview}
              style={{ marginTop: '0.75rem', background: 'none', border: '1px solid #e74c3c', color: '#e74c3c', borderRadius: '8px', padding: '0.5rem 1rem', cursor: 'pointer', fontWeight: '600' }}>
              Intentar con otro archivo
            </button>
          </div>
        )}

        {preview && (
          <div style={{ background: '#e8f5e9', border: `2px solid ${preview.hasData ? '#27ae60' : '#ff9800'}`, borderRadius: '12px', padding: '1.25rem', marginTop: '1rem' }}>
            <strong style={{ color: preview.hasData ? '#2e7d32' : '#e67e22', fontSize: '1rem' }}>
              {preview.hasData ? '✅' : '⚠️'} Archivo reconocido — {preview.tripCount} viaje{preview.tripCount !== 1 ? 's' : ''} encontrado{preview.tripCount !== 1 ? 's' : ''}
            </strong>
            <ul style={{ color: '#555', fontSize: '0.9rem', marginTop: '0.75rem', paddingLeft: '1.25rem', lineHeight: '1.8' }}>
              {preview.tripNames.map((name, i) => <li key={i}>{name}</li>)}
              {preview.tripCount > 5 && <li style={{ opacity: 0.6 }}>...y {preview.tripCount - 5} más</li>}
            </ul>
            {preview.exportDate && (
              <p style={{ color: '#666', fontSize: '0.85rem', marginTop: '0.5rem' }}>
                Exportado: {(() => { try { return new Date(preview.exportDate).toLocaleString('es-ES'); } catch (_) { return preview.exportDate; } })()}
              </p>
            )}
            {!preview.hasData && (
              <div style={{ background: '#fff3e0', border: '1px solid #ff9800', borderRadius: '8px', padding: '0.75rem', marginTop: '0.75rem' }}>
                <strong style={{ color: '#e67e22', fontSize: '0.9rem' }}>⚠️ Este archivo no contiene notas ni actividades</strong>
                <p style={{ color: '#666', fontSize: '0.85rem', margin: '0.4rem 0 0', lineHeight: '1.5' }}>
                  Parece ser el archivo guardado al crear el viaje, antes de añadir datos.
                  Si ya tienes notas en el viaje, <strong>no las sobrescribirá</strong> — solo actualizará los datos del viaje (destino, fechas).
                </p>
              </div>
            )}
            <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1rem' }}>
              <ActionButton color={preview.hasData ? 'green' : 'amber'} onClick={handleConfirmRestore} disabled={restoring} style={{ flex: 1 }}>
                {restoring ? '⏳ Restaurando...' : preview.hasData ? '✅ Restaurar ahora' : '⚠️ Continuar de todas formas'}
              </ActionButton>
              <button onClick={handleCancelPreview}
                style={{ padding: '1rem 1.25rem', borderRadius: '12px', border: '2px solid #ccc', background: 'white', color: '#666', fontWeight: '600', cursor: 'pointer' }}>
                Cancelar
              </button>
            </div>
          </div>
        )}

        <div style={{ background: '#fff3e0', padding: '1rem', borderRadius: '8px', marginTop: '1rem', borderLeft: '4px solid #ff9800' }}>
          <p style={{ color: '#555', fontSize: '0.85rem', margin: 0, lineHeight: '1.5' }}>
            <strong>ℹ️</strong> Los viajes del backup se añadirán a los actuales.
            Si ya existe un viaje con el mismo ID, se actualizará.
          </p>
        </div>
      </div>

      {/* Shadow backup recovery — only shown if shadow exists */}
      {shadowInfo && (
        <div style={{ marginBottom: '1.5rem' }}>
          <STitle text="Recuperación desde Memoria del Navegador" />
          <Card bg="#fff8e1" border="#f39c12">
            <p style={{ color: '#555', fontSize: '0.9rem', margin: '0 0 1rem', lineHeight: '1.6' }}>
              El app guarda automáticamente una copia de tus viajes en la memoria del navegador.
              Esta copia <strong>no incluye fotos</strong> pero preserva todos los datos de texto
              (notas, presupuestos, listas, alojamientos, transporte).
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '1rem', fontSize: '0.9rem' }}>
              <div>
                <strong>Guardado:</strong>
                <div style={{ color: '#666' }}>{shadowInfo.savedAt.toLocaleString('es-ES')}</div>
              </div>
              <div>
                <strong>Viajes en memoria:</strong>
                <div style={{ color: '#666' }}>{shadowInfo.tripCount}</div>
              </div>
            </div>
            {shadowInfo.contentTrimmed && (
              <p style={{ color: '#e67e22', fontSize: '0.8rem', marginBottom: '0.75rem' }}>
                ⚠️ Notas y reflexiones se omitieron para ahorrar espacio.
              </p>
            )}
            <ActionButton color="amber" onClick={handleShadowRestore} disabled={restoring}>
              {restoring ? '⏳ Recuperando...' : '🧠 Recuperar desde Memoria del Navegador'}
            </ActionButton>
          </Card>
        </div>
      )}

      {/* Storage protection */}
      {storageInfo && (
        <div style={{ marginBottom: '1.5rem' }}>
          <STitle text="Protección de Almacenamiento" />
          <Card
            bg={storageInfo.isPersisted ? '#e8f5e9' : '#fff3e0'}
            border={storageInfo.isPersisted ? '#4caf50' : '#ff9800'}
          >
            <div style={{ marginBottom: '0.75rem' }}>
              {storageInfo.isPersisted
                ? <span style={{ color: '#4caf50' }}><strong>✅ Almacenamiento Protegido</strong></span>
                : <span style={{ color: '#ff9800' }}><strong>⚠️ Sin Protección Automática</strong></span>
              }
            </div>
            <p style={{ color: '#555', fontSize: '0.9rem', margin: 0, lineHeight: '1.5' }}>
              {storageInfo.isPersisted
                ? 'El navegador no borrará tus datos automáticamente.'
                : 'Tu navegador podría borrar datos si se queda sin espacio. Haz backups regulares.'}
            </p>
          </Card>

          {!storageInfo.isPersisted && storageInfo.isSupported && (
            <ActionButton color="purple" onClick={handleRequestPersistence} disabled={loading}
              style={{ marginBottom: '1.5rem' }}>
              🛡️ Activar Protección
            </ActionButton>
          )}

          <div style={{ marginTop: '0.5rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
              <span style={{ fontSize: '0.9rem', color: '#666' }}>Espacio usado:</span>
              <span style={{ fontSize: '0.9rem', color: '#666', fontWeight: '600' }}>
                {storageInfo.usage?.formatted} / {storageInfo.quota?.formatted}
              </span>
            </div>
            <div style={{ background: '#f0f0f0', height: '12px', borderRadius: '6px', overflow: 'hidden' }}>
              <div style={{
                background: parseFloat(storageInfo.percentage) > 70
                  ? 'linear-gradient(90deg,#e74c3c 0%,#c0392b 100%)'
                  : 'linear-gradient(90deg,#B8A082 0%,#D4C4A8 100%)',
                height: '100%',
                width: `${Math.min(storageInfo.percentage, 100)}%`,
                transition: 'width .3s ease',
              }} />
            </div>
            <p style={{ fontSize: '0.85rem', color: '#666', marginTop: '0.5rem' }}>
              {storageInfo.status?.icon} {storageInfo.status?.message}
            </p>
          </div>
        </div>
      )}

      {/* Reminders toggle */}
      <div style={{ marginBottom: '1.5rem' }}>
        <STitle text="Recordatorios" />
        <button
          onClick={handleToggleBackups}
          style={{
            background: backupStatus?.disabled ? '#e74c3c' : '#27ae60',
            color: 'white', border: 'none', padding: '1rem 2rem', borderRadius: '12px',
            fontSize: '1rem', fontWeight: '600', cursor: 'pointer', width: '100%', transition: 'all .2s',
          }}
        >
          {backupStatus?.disabled ? '🔴 Activar Recordatorios' : '🟢 Desactivar Recordatorios'}
        </button>
        <p style={{ color: '#666', fontSize: '0.85rem', marginTop: '0.75rem', lineHeight: '1.5' }}>
          {backupStatus?.disabled
            ? 'Los recordatorios están desactivados.'
            : 'Recibirás un recordatorio cada 7 días para hacer backup.'}
        </p>
      </div>

      {/* Tips */}
      <Card border="#B8A082">
        <h4 style={{ color: '#8B7355', marginBottom: '0.75rem', fontSize: '1rem' }}>Consejos de Seguridad</h4>
        <ul style={{ margin: 0, paddingLeft: '1.5rem', color: '#666', fontSize: '0.9rem', lineHeight: '1.8' }}>
          <li>Haz backups cada 7-14 días o después de viajes importantes</li>
          <li>Guarda los backups en tu nube personal (Drive, Dropbox, iCloud)</li>
          <li>Los backups incluyen todas tus fotos en formato base64</li>
          <li>Si cambias de dispositivo, importa el backup en el nuevo</li>
          <li>La copia en memoria del navegador es una red de seguridad, no un sustituto del backup</li>
        </ul>
      </Card>
      </>)}

    </div>
  );
};

export default BackupSettings;
